import { Icon } from '@dynamic/ui';
import styled, { css } from 'styled-components';

import { ThemeConfig } from '#/service/config';
import { horizontalDirection } from '#/service/constants';
import { handleOpacity, remify } from '#/service/utils';

import { fixFocusInSafari } from '../../common';
import { StyledInputProps, StyledTextAreaLabel } from '../Form.types';
import { InputLabelProps, StyledIconProps } from './FormInput.types';

export const cssInputBase = (
  highlighted: boolean,
  hasLeftIcon = false,
  hasRightIcon = false,
  theme: ThemeConfig
) => css`
  height: 100%;
  width: 100%;
  font: var(--fontStyleBody);
  box-sizing: border-box;
  color: ${highlighted
    ? theme.color.inputForeground
    : handleOpacity(theme.color.inputForeground, 0.6)};
  background-color: var(--inputBackground);
  border-radius: var(--inputBorderRadius);
  padding: ${remify(17)} ${remify(hasRightIcon ? 44 : 12)} ${remify(3)} ${remify(hasLeftIcon ? 44 : 12)};
`;

export const Container = styled.div`
  margin: ${remify(20)} 0;
  position: relative;

  &:first-of-type {
    margin-top: 0;
  }
`;

export const InputContainer = styled.div`
  position: relative;
  height: ${remify(50)};
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const InputLabel = styled.label<InputLabelProps>`
  position: absolute;
  ${({ $bigLabel }) => !$bigLabel && `top: 0;`}
  left: ${({ $hasLeftIcon }) => remify($hasLeftIcon ? 44 : 12)};
  right: ${({ $hasRightIcon }) => remify($hasRightIcon ? 44 : 5)};
  padding: ${remify(4)} 0;
  font: ${({ $bigLabel }) =>
    $bigLabel ? `var(--fontStyleBody)` : `var(--fontStyleCaption1)`};
  color: ${({ $highlighted, theme }) =>
    $highlighted
      ? theme.color.inputForeground
      : handleOpacity(theme.color.inputForeground, 0.6)};
  cursor: text;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

export const StyledIcon = styled(Icon)<StyledIconProps>`
  position: absolute;
  top: ${remify(13)};

  ${({ position, $highlighted, theme }) => {
    const isRight = position === horizontalDirection.RIGHT;

    return `
      color: ${
        $highlighted && isRight
          ? theme.color.inputForeground
          : handleOpacity(
              theme.color.inputForeground,
              $highlighted || isRight ? 0.6 : 0.4
            )
      };
      ${position}: ${remify(12)};
      ${isRight ? 'cursor: pointer;' : ''}`;
  }}
`;

export const StyledInput = styled('input')<StyledInputProps>`
  ${({ $highlighted, $hasLeftIcon, $hasRightIcon, theme }) =>
    cssInputBase($highlighted, $hasLeftIcon, $hasRightIcon, theme)}
  border: none;

  &:invalid {
    ${({ $touched }) =>
      $touched && `box-shadow: inset 0 0 0 2px var(--inputValidation);`}
  }

  &:focus {
    outline: 1px solid var(--itemFocusFrame);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fixFocusInSafari}
`;

export const TextAreaContainer = styled.div`
  position: relative;
  height: ${remify(136)};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const StyledTextArea = styled.textarea<StyledInputProps>`
  ${({ $highlighted, $hasLeftIcon, theme, $hasRightIcon }) =>
    cssInputBase($highlighted, $hasLeftIcon, $hasRightIcon, theme)}
  border: none;
  min-height: ${remify(136)};
  min-width: 100%;
  padding: ${remify(12)};
  resize: none;

  /*  Firefox scrollbar  */
  scrollbar-color: ${({ theme }) =>
      handleOpacity(theme.color.inputForeground, 0.6)}
    transparent;

  /* Webkit scrollbars, Chrome, Edge, Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) =>
      handleOpacity(theme.color.inputForeground, 0.6)};
    border: 1px solid var(--inputBackground);
    border-radius: 20px;
  }

  &:invalid {
    ${({ $touched }) =>
      $touched && `box-shadow: inset 0 0 0 2px var(--inputValidation);`}
  }

  &:focus {
    outline: 1px solid var(--itemFocusFrame);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fixFocusInSafari}
`;

export const TextAreaLabel = styled.label<StyledTextAreaLabel>`
  position: absolute;
  display: ${({ $hide }) => ($hide ? 'none' : 'flex')};
  margin: ${remify(12)};
  font: var(--fontStyleBody);
  color: ${({ $highlighted, theme }) =>
    $highlighted
      ? theme.color.inputForeground
      : handleOpacity(theme.color.inputForeground, 0.6)};
  cursor: text;
  text-align: left;
  max-width: 100%;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
